'use client'

import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useState, useTransition } from 'react'

import { RentalWindowPicker } from '@/components/molecules/rental-window-picker.client'
import { creatorHref } from '@/lib/creator'
import { formatDay } from '@/lib/dates'
import {
  dayWord,
  lastHeldDay,
  type RentalItemDetailView,
  type RentalTermView,
  returnDay,
} from '@/lib/rental'
import { PICKED_FILL, RADIUS, SURFACE } from '@/lib/surface'
import { BODY, META, MICRO, STAT } from '@/lib/typography'
import { cn } from '@/lib/utils'
import { addLineAction } from '@/server/order-lines'

/**
 * The rail: what it costs, for how long, and the way into the basket.
 *
 * This is the one part of the detail page that has to be a client island — a
 * date changes a price without a round trip — and it is deliberately the ONLY
 * one. Everything else on the page is a server component.
 *
 * ## A LENGTH and a pick-up day, not a range (spec §11 · I4)
 *
 * Spec §11 sells five lengths — 1, 3, 7, 14, 30 days — and says "inne długości
 * niedozwolone". A range calendar accepts five days; the checkout does not, so
 * the calendar was offering something that could only be refused later. What
 * it asks for now is the day the gear is collected, and which of the five
 * lengths; the return date follows.
 *
 * ## It quotes; it does not reserve
 *
 * The figure is the CATALOGUE'S price for that length, handed to `/pl/kreator`
 * with the variant. It does not check availability, because nothing on the
 * order path reads `offering_availability` or `stock_quantity`, and §4 of the
 * regulamin says a chosen date is a preference until FLOU confirms it. The line
 * under the button says exactly that — next to the control that takes the date,
 * not three sections away.
 *
 * ## It hands off rather than adding to the basket
 *
 * `/pl/kreator` is the only place in the app where anything enters the cart,
 * and adding a line has exactly one call site. A second one here would
 * have to reproduce the variant slug, the quote-only flag, the quantity
 * normalisation and a line signature that folds in the booking term — four
 * chances for the basket to show a total the placed order contradicts. The
 * `variantSlug` travels in the link so the creator lands on the right rate.
 */
export function RentalBookingPanel({
  item,
  locale,
  className,
}: {
  item: RentalItemDetailView
  locale: string
  className?: string
}) {
  const [from, setFrom] = useState('')
  const [picked, setPicked] = useState<RentalTermView | null>(null)
  const router = useRouter()
  const [adding, startAdding] = useTransition()
  const shown = picked ?? item.terms[0] ?? null

  /**
   * The term the reader actually chose, or the item's own default.
   *
   * Named once because the button now does two things with it — builds the
   * address and writes the line — and two spellings of "which variant" is
   * exactly how a basket ends up holding something the page did not offer.
   */
  const variantSlug = picked?.variantSlug ?? item.variantSlug ?? null
  const to = picked ? lastHeldDay(from, picked.days) : ''

  /**
   * The day the gear comes back — and deliberately NOT `to`.
   *
   * They are one day apart and both are correct, because they answer different
   * questions. `to` is the LAST DAY OF HIRE, which is what the order stores and
   * what `countDays` measures: it counts inclusively (`(b − a) / 86 400 000 + 1`),
   * so a 3-day hire collected on the 1st runs 1–3 and `to` is the 3rd. Order
   * validation compares that count against the chosen variant's term and
   * refuses the line when they disagree, so `to` cannot be nudged forward to
   * make this sentence easier to write.
   *
   * What the reader asks, though, is "when do I bring it back?", and the answer
   * is the 4th — because a doba is 24 h counted from the hour of collection
   * (the same rule `RentalTerms` states under "Jak to działa"), so three of them
   * from the 1st expire on the 4th. Hence `+ days`, shown only, stored never.
   */
  const returnOn = picked ? returnDay(from, picked.days) : ''
  const href = creatorHref(locale, {
    offeringSlug: item.slug,
    ...(variantSlug ? { variantSlug } : {}),
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
  })

  return (
    <aside
      className={cn(
        'flex flex-col p-5 sm:p-6',
        '@container relative overflow-hidden',
        RADIUS.feature,
        SURFACE,
        // Below `lg` the frame comes straight back off.
        //
        // A panel earns its border by being an object among other objects —
        // which is what it is at `lg`, where it sits in the second column
        // beside the copy. Stacked on a phone it spans the whole viewport, so
        // the border draws a box around content that already fills the screen
        // and the padding insets it from a page edge it should be aligned to.
        // Stripped, the price, the date picker and the CTA line up with the
        // breadcrumb, the heading and the description above them, and read as
        // part of the page rather than as a widget dropped onto it.
        'max-lg:rounded-none max-lg:border-0 max-lg:bg-transparent max-lg:p-0 max-lg:backdrop-blur-none',
        className,
      )}
    >
      {/* The light every other surface on this site has and this one did not.
          Same ramp and same corner as the tier cards' — a panel that asks for
          money on flat black reads as a settings box. */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-40"
        style={{
          background:
            'radial-gradient(120% 100% at 24% 0%, oklch(0.66 0.25 320 / 0.16), transparent 70%)',
        }}
      />

      {/* The item's name lives IN the panel now (owner's mock-up A): the page
          opens on the photograph and this column, with no headline band above
          them, so the thing you are renting and its price read as one block. */}
      <p className={cn(MICRO, 'relative text-white/55')}>{item.categoryLabel}</p>
      <h1 className="relative mt-2 font-masthead text-[clamp(1.9rem,11cqw,2.4rem)] leading-[1.02] font-bold tracking-[-0.03em] text-white">
        {item.name}
      </h1>

      {/* THE PRICE IS THE HEADLINE of the transaction, and it is always the
          price of a TERM — the shortest one until the reader picks another.
          It used to fall back to the daily rate labelled „/ doba (24 h)",
          which on an item sold from three days up printed the three-day price
          as if it were one day's. */}
      {shown ? (
        <div className="relative mt-5 border-t border-white/10 pt-5">
          <p className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
            <span className="font-masthead text-[clamp(2.6rem,20cqw,3.8rem)] leading-[0.95] font-bold tracking-[-0.04em] tabular-nums text-white">
              {Math.round(shown.priceNet)} zł
            </span>
            <span className="font-masthead text-[1.4rem] font-semibold text-white">+ VAT</span>
          </p>
          <p className={cn(META, 'mt-1.5 text-white/60')}>
            za {shown.days} {dayWord(shown.days)} · {Math.round(shown.priceNet / shown.days)} zł /
            doba
          </p>
        </div>
      ) : (
        <div className="relative mt-5 border-t border-white/10 pt-5">
          <p className={cn(STAT, 'text-white')}>Wycena indywidualna</p>
          <p className={cn(META, 'mt-1.5 text-white/60')}>Odezwiemy się z wyceną po zapytaniu.</p>
        </div>
      )}

      {/* Spec §11's five lengths, as five buttons — in a GRID, not a wrap.
          Five pills of unequal width („Doba" against „30 dób") broke 4 + 1 at
          this column's width, which reads as a row that ran out of room rather
          than as a set of five choices. Three equal columns make it 3 + 2 on
          purpose, and every pill the same size, which is what a set of
          alternatives should look like. */}
      {item.terms.length > 0 ? (
        <div
          className={cn(
            'relative mt-5 grid gap-2',
            item.terms.length === 4 ? 'grid-cols-4' : 'grid-cols-3',
          )}
        >
          {item.terms.map((row) => (
            <button
              key={row.days}
              type="button"
              onClick={() => setPicked(row)}
              aria-pressed={picked?.days === row.days}
              className={cn(
                META,
                'min-h-[3.25rem] rounded-2xl border px-2 font-masthead text-[0.95rem] font-semibold transition-colors',
                picked?.days === row.days
                  ? PICKED_FILL
                  : 'border-white/12 bg-white/[0.03] text-white hover:border-white/30',
              )}
            >
              {row.label}
            </button>
          ))}
        </div>
      ) : null}

      {/* Both ends of the hire, in one control (spec §11 · I4).
          It used to be a single day picker beside the length pills, which asked
          the reader to do the arithmetic themselves: take it on the 12th, pick
          "3 doby", and work out for yourself when it is due back. The calendar
          answers that by showing it — pick the collection day and the only days
          left live are the five the price list has. */}
      <div className="mt-3">
        <RentalWindowPicker
          terms={item.terms}
          from={from}
          picked={picked}
          onPick={(day, term) => {
            setFrom(day)
            setPicked(term)
          }}
        />
      </div>

      {/* Directly under the picker, because it is the answer to what was just
          typed there — and it needs BOTH controls, so it appears once a length
          and a collection day exist and re-reads whenever either changes.
          Naming the rule ("do tej samej godziny") is what stops the date
          looking like an off-by-one: three doby from the 1st ending on the 4th
          only adds up once the reader knows a doba runs 24 h from collection.

          The element is always mounted, `sr-only` while empty rather than
          absent, so it is a live region that already exists when the text
          arrives — a region mounted at the same moment as its first content is
          not reliably announced. Both controls that feed it can be operated
          without ever moving focus here, the date one from inside a dialog. */}
      <p aria-live="polite" className="sr-only">
        {returnOn
          ? `Odbiór ${formatDay(from)}, zwrot ${formatDay(returnOn)} do tej samej godziny.`
          : ''}
      </p>

      <Link
        href={href}
        onClick={(event) => {
          // Plain navigation for the cases a browser owns: a new tab, a saved
          // link, a middle click. Only the ordinary left click is taken over.
          if (!variantSlug || adding) return
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return
          event.preventDefault()
          startAdding(async () => {
            await addLineAction({
              offeringSlug: item.slug,
              variantSlug,
              citySlug: null,
              // A hire is one piece of gear on one term — the window IS the
              // quantity, which is why the creator draws no stepper for it.
              qty: 1,
              from,
              to,
              time: '',
              brief: '',
              sourceUrl: '',
              lengthBand: null,
              footageMinutes: '',
              fromFilmLineId: null,
            })
            router.push(href)
          })
        }}
        // Solid white, not the brand gradient. On an inner page the gradient
        // was decoration competing with the price above it; a flat fill is the
        // loudest thing on a dark page precisely because nothing else is
        // filled, so the primary action reads as primary without a hue.
        className={cn(
          BODY,
          'mt-5 inline-flex min-h-12 items-center justify-center rounded-full bg-white px-6 font-semibold text-neutral-900 transition-colors duration-200 hover:bg-white/90',
          adding && 'pointer-events-none opacity-70',
        )}
      >
        {adding ? 'Dodaję…' : picked ? 'Wypożycz na ten termin' : 'Przejdź do zamówienia'}
      </Link>

      <p className={cn(MICRO, 'mt-3 text-center text-white/55')}>
        Termin potwierdzamy odpowiedzią. Bez kaucji.
      </p>
    </aside>
  )
}
